import { SentryError } from '@ifixit/sentry';
import type { ProductID, ProductVariantID } from './type-helpers';

interface Reviewable {
   rating: number;
   count: number;
}

/**
 * Product identifier nomenclature (canonical model: Objects/Cart/ProductSku.php):
 * - itemcode: the dashed display form `IF{xxx}-{yyy}-{z}`, e.g. "IF145-307-4".
 * - sku: the numeric database form `{productcode}{optionid}` with no
 *   separators, e.g. "1453074". External data (e.g. Shopify variant SKU
 *   fields) may carry either form.
 * - productcode: the first six digits ("145307"), shared by all variants
 *   of a product.
 * - optionid: the trailing one or two digits ("4"), identifying a variant
 *   within the product.
 * - category: the first three digits of the productcode ("145").
 */
const ITEMCODE_RE = /IF(\d{3})-(\d{3})-(\d{1,2})/;

export function shouldShowProductRating<R extends Reviewable>(
   reviewable: R | null | undefined
): reviewable is R {
   if (reviewable?.rating == null || reviewable?.count == null) {
      return false;
   }
   return reviewable.rating >= 4 || reviewable.count > 10;
}

/**
 * Formats a digit string as an itemcode by prefixing "IF" and inserting a
 * dash after every third digit: "1453074" → "IF145-307-4", and a bare
 * productcode "145307" → "IF145-307". The input must be digits only — an
 * already-dashed itemcode is not passed through but mangled.
 */
export function getItemCodeFromSku(sku: string): string {
   const itemCode = `IF${sku.replace(/(.{3})/g, '$1-')}`;
   return itemCode.endsWith('-') ? itemCode.slice(0, -1) : itemCode;
}

/**
 * Extracts the productcode from a loosely-formatted identifier: full
 * itemcode ("IF145-307-4"), itemcode without optionid ("IF145-307"), or
 * numeric sku ("1453074"). Returns null when the input doesn't look like
 * either form.
 */
export function productcodeFromSku(sku: string): string | null {
   const digits = sku.trim().replace(/^if/i, '').replace(/\D/g, '');
   if (digits.length < 6 || digits.length > 8) return null;
   return digits.slice(0, 6);
}

/**
 * Splits an itemcode into category, productcode, and optionid. Only
 * matches the full dashed form ("IF145-307-4"): returns {} for numeric
 * skus and for itemcodes without an optionid ("IF145-307").
 */
export function parseItemcode(itemcode: string) {
   const matches = itemcode.match(ITEMCODE_RE);
   return matches
      ? {
           category: matches[1],
           productcode: matches[1] + matches[2],
           optionid: matches[3],
        }
      : {};
}

const LIFETIME_WARRANTY_TRANSLATIONS = [
   'lifetime warranty',
   'lifetime guarantee',
   '25 year guarantee',
   '25 ans de garantie',
   '25 jahre garantie',
   '25 anni di garanzia',
   '25 años de garantía',
   'lebenslange garantie',
   'garantie à vie',
   'ömür boyu garanti',
   'garantia vitalícia',
   'garanzia a vita',
   'garantía de por vida',
   '生涯保証',
   '终生保修',
   'levenslange garantie',
   '평생 품질 보증',
] as const;

export function isLifetimeWarranty(warranty: string | undefined | null): boolean {
   if (!warranty) return false;

   const normalized = warranty.toLowerCase();
   return LIFETIME_WARRANTY_TRANSLATIONS.some(translation => normalized.includes(translation));
}

export function getVariantIdFromVariantURI(variantURI: ProductVariantID): string {
   if (!variantURI.startsWith('gid://')) {
      throw new SentryError('Variant URI must be a global shopify product variant id uri');
   }
   return variantURI.replace(/^gid:\/\/shopify\/ProductVariant\//, '');
}

export function getProductIdFromGlobalId(globalProductId: ProductID) {
   if (!globalProductId.startsWith('gid://')) {
      throw new SentryError('globalProductId must be a global shopify product id uri');
   }
   return globalProductId.replace(/^gid:\/\/shopify\/Product\//, '');
}

/**
 * @param globalId Like gid://shopify/Order/4725063778394?key=abc123
 */
export function decodeGlobalId(globalId: string): number {
   const matches = globalId.match(/gid:\/\/shopify\/\w+\/(\d+)/);
   const id = parseInt(matches?.[1] ?? '');
   if (isNaN(id)) {
      throw new SentryError(`Invalid Shopify global id "${globalId}"`);
   }
   return id;
}

export function getProductVariantURI(variantId: string | number): ProductVariantID {
   return `gid://shopify/ProductVariant/${variantId}` as ProductVariantID;
}

export function getProductURI(productId: string | number): ProductID {
   return `gid://shopify/Product/${productId}` as ProductID;
}

export function getCategoriesFromTags(tags: string[]): string[] {
   const tagsObj: Record<string, string> = {};
   tags.forEach(tag => {
      const [name, val] = tag.split(/=|:/);
      tagsObj[name] = val;
   });
   const categories = [
      'Tool' in tagsObj ? 'Tool' : 'Part' in tagsObj ? 'Part' : 'N/A',
      tagsObj['Item Category'],
      tagsObj['Part SubCategory'],
   ];
   return categories.map(category => category ?? 'N/A');
}
